import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';

import { AdminedUser } from '@/admin/types/admined-user.dto';

import t from '@/utils/translate';
import Toolbar from '@/components/Toolbar';
import FilterTabsBar from '@/components/FilterTabsBar';
import FilterTabButton from '@/components/FilterTabButton';
import SearchInput from '@/components/SearchInput';
import MarkerIcon from '@/components/icons/Marker';
import TableList, { ColumnMeta } from '@/components/TableList';

import UserCard from '@/components/UserCard';
import DateCard from '@/components/DateCard';

import reducer, { ROW_HEIGHT } from './reducer';
import { UserStatus } from '@bimdata/definitions/const';
import BulkOperationButton from '@/components/BulkOperationButton';
import { AmountsLabel } from '@/components/AmountLabel';
import TrashBasket from '@/components/icons/TrashBasket';
import { deleteUsers, setStatus } from '@/admin/api/common';

const idGetter = (au: AdminedUser) => au.id;

type BulkButtonProps = {
  selected: AdminedUser[],
  onClick: (candidates: AdminedUser[]) => Promise<void>,
  candidateFilterCriteria: (u: AdminedUser) => boolean
} & PropsWithChildren;

const BulkButton = ({
  selected, onClick, children, candidateFilterCriteria
}: BulkButtonProps) => {
  const candidates = useMemo(
    () => selected.filter(candidateFilterCriteria),
    [ selected, candidateFilterCriteria ]
  );

  const clickHandler = useCallback(
    () => onClick(candidates),
    [ onClick, candidates ]
  );

  return (
    <BulkOperationButton onClick={clickHandler} disabled={!candidates.length}>
      {children}
      {!!candidates.length && <AmountsLabel>{candidates.length}</AmountsLabel>}
    </BulkOperationButton>
  );
}

export default () => {
  const [state, dispatch] = reducer();
  const [selected, setSelected] = useState<AdminedUser[]>([]);

  const setStatusFilter = useCallback((value: UserStatus | null) => {
    dispatch({ type: 'setStatusFilter', payload: value });
  }, [ dispatch ]);

  const onSearchChange = useCallback((value: string) => {
    dispatch({ type: 'setSearch', payload: value });
  }, [ dispatch ]);

  const sortingHandler = useCallback((sortBy: keyof AdminedUser, sortOrder: 'A' | 'D') => {
    dispatch({ type: 'setSorting', payload: [sortBy, sortOrder] });
  }, [ dispatch ]);

  const loadNextPage = useCallback(async (offset: number, size: number) => {
    dispatch({ type: 'loadWithOffset', payload: offset });
  }, [ dispatch ]);

  const ensureRow = useCallback((item: AdminedUser) => {
    dispatch({
      type: 'ensure',
      payload: item.id
    });
  }, [ dispatch ]);

  const onTableListResize = useCallback((e: HTMLElement) => {
    dispatch({
      type: 'setPageSize',
      payload: e.getBoundingClientRect().height * 1.5 / ROW_HEIGHT,
    });
  }, [dispatch]);

  const setUsersStatusHandler = useCallback(async (users: AdminedUser[], status: UserStatus) => {
    const updated = await setStatus(users.map((u) => u.id), status);
    if (!updated.length) {
      return;
    }
    dispatch({
      type: 'setUsersStatus',
      payload: { ids: new Set(updated), status}
    });
  }, [ dispatch ]);

  const activateHandler = useCallback(
    async (users: AdminedUser[]) => setUsersStatusHandler(users, UserStatus.ACTIVE),
    [ setUsersStatusHandler ]
  );

  const blockHandler = useCallback(
    async (users: AdminedUser[]) => setUsersStatusHandler(users, UserStatus.BLOCKED),
    [ setUsersStatusHandler ]
  );

  const deleteHandler = useCallback(async (users: AdminedUser[]) => {
    const deleted = await deleteUsers(users.map((u) => u.id));
    if (!deleted.length) {
      return;
    }
    dispatch({
      type: 'deleteUsers',
      payload: new Set(deleted)
    });
  }, [ dispatch ]);

  const rowStyle = useCallback((u: AdminedUser) => {
    if (state.params.statusFilter === null) {
      if (u.status === UserStatus.GONE || u.status === UserStatus.BLOCKED) {
        return { opacity: 0.5 };
      }
    }
    return undefined;
  }, [state.params.statusFilter]);

  const columns = useMemo<ColumnMeta[]>(() => ([
    {
      attrName: 'fullName',
      title: t('admin.user.column.name'),
      dataGetter: (item: AdminedUser) => <UserCard profile={item} avatar={state.avatars[item.id]} blocked={item.status === UserStatus.BLOCKED}/>,
    },
    /*{
      width: 50,
      title: t('admin.user.column.status'),
    },*/
    {
      width: 90,
      attrName: 'ownerships',
      title: t('admin.user.column.owning'),
      defaultSorting: 'D',
    },
    {
      width: 90,
      attrName: 'participations',
      title: t('admin.user.column.participation'),
      defaultSorting: 'D',
    },
    /*{
      width: 145,
      title: t('admin.user.column.lastActivity'),
    },*/
    {
      width: 150,
      attrName: 'registered',
      title: t('admin.user.column.registered'),
      dataGetter: (item: AdminedUser) => <DateCard value={item.registered} />,
      defaultSorting: 'D',
      style: { textAlign: 'right' },
    },
    {
      width: 30,
      type: 'iconic',
      attrName: 'status',
      dataGetter: (item: AdminedUser) => {
        switch (item.status) {
          case UserStatus.INACTIVE: return <MarkerIcon size={20} color="#FF8B00"/>
          case UserStatus.ACTIVE: return <MarkerIcon size={20} color="#00875A"/>
          case UserStatus.GONE: return <MarkerIcon size={20} color="#DE350B"/>
          case UserStatus.BLOCKED: return <MarkerIcon size={20} color="#C1C7D0"/>
        }
      },
    },
    { width: 40 }
  ]), [state.list, state.avatars ]);

  return <>
    <Toolbar>
      <FilterTabsBar>
        <FilterTabButton
          value={null}
          filter={state.params.statusFilter}
          onClick={setStatusFilter}
          score={state.stats.total}
        >
          {t('admin.user.filter.all')}
        </FilterTabButton>
        <FilterTabButton
          value={UserStatus.INACTIVE}
          filter={state.params.statusFilter}
          onClick={setStatusFilter}
          score={state.stats[UserStatus.INACTIVE]}
        >
          {t('admin.user.filter.inactive')}
        </FilterTabButton>
        <FilterTabButton
          value={UserStatus.ACTIVE}
          filter={state.params.statusFilter}
          onClick={setStatusFilter}
          score={state.stats[UserStatus.ACTIVE]}
        >
          {t('admin.user.filter.active')}
        </FilterTabButton>
        <FilterTabButton
          value={UserStatus.BLOCKED}
          filter={state.params.statusFilter}
          onClick={setStatusFilter}
          score={state.stats[UserStatus.BLOCKED]}
        >
          {t('admin.user.filter.blocked')}
        </FilterTabButton>
        <FilterTabButton
          value={UserStatus.GONE}
          filter={state.params.statusFilter}
          onClick={setStatusFilter}
          score={state.stats[UserStatus.GONE]}
        >
          {t('admin.user.filter.gone')}
        </FilterTabButton>
      </FilterTabsBar>
      <SearchInput
        onChange={onSearchChange}
        value={state.params.search}
        placeholder={t('action.search')}
      />
    </Toolbar>
    {!state.list && <LinearProgress />}
    {state.list && (
      <TableList<AdminedUser>
        nextPageThreshold={0.1}
        sorting={state.params.sorting}
        columns={columns}
        data={(state.listSize !== null) ? state.list : undefined}
        onViewportResize={onTableListResize}
        pageSize={state.pageSize}
        onDataRequest={state.pageSize ? loadNextPage : undefined}
        onRowVisible={ensureRow}
        onSortingChange={sortingHandler}
        idGetter={idGetter}
        isCheckList={true}
        checkListChange={setSelected}
        selectionStatusCaption={t('admin.user.selectionCaption')}
        rowStyle={rowStyle}
      >
        <BulkButton
          selected={selected}
          candidateFilterCriteria={(u) => u.status !== UserStatus.ACTIVE}
          onClick={activateHandler}
        >
          <MarkerIcon size={20} color="#00875A"/>
          {t('admin.user.actions.activate')}
        </BulkButton>
        <BulkButton
          selected={selected}
          candidateFilterCriteria={(u) => (
            u.status === UserStatus.ACTIVE || u.status === UserStatus.INACTIVE
          )}
          onClick={blockHandler}
        >
          <MarkerIcon size={20} color="#C1C7D0"/>
          {t('admin.user.actions.block')}
        </BulkButton>
        <BulkButton
          selected={selected}
          candidateFilterCriteria={(u) => (
            u.status === UserStatus.BLOCKED || u.status === UserStatus.GONE
          )}
          onClick={deleteHandler}
        >
          <TrashBasket size={20} color="#DE350B"/>
          {t('admin.user.actions.delete')}
        </BulkButton>
      </TableList>
    )}
  </>;
};
